// Bulk field-delete visibility. ReportStructureGuard deliberately allows
// deleting chips and sections ("a legitimate rad workflow"), so a drag
// selection spanning several findings + any keystroke removes them all in
// one silent transaction — undo recovers, but nothing announces that it
// happened (pilot: "you can make a mistake and accidentally delete
// something you're not intending to"). This module makes the bulk case
// loud without adding friction: deletion proceeds untouched and a toast
// with an inline Undo appears.
//
// Programmatic full-content rebuilds (draft reset, impression re-stream)
// replace many chips in one transaction by design — those call sites tag
// their transaction with BULK_DELETE_TOAST_SKIP_META so only rad-initiated
// edits can toast.

import type { Editor } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";
import { emit } from "../../lib/viewerBus";

export const BULK_DELETE_TOAST_SKIP_META = "bulkFieldDeleteToastSkip";

// A single transaction removing this many field nodes triggers the toast.
// Below it (single-chip dismiss, two-chip cleanup) deletion is assumed
// deliberate — the threshold exists to catch sweeps, not edits.
const BULK_FIELD_DELETE_TOAST_THRESHOLD = 3;

// Undo needs spotting + travel time; the default 3.5s dismiss is tuned for
// glanceable status lines.
const UNDO_TOAST_DURATION_MS = 6000;

export function countFieldNodes(doc: ProseMirrorNode): number {
  let n = 0;
  doc.descendants((node) => {
    if (node.type.name === "field") n += 1;
  });
  return n;
}

/**
 * Subscribe to the editor's update stream and toast (with Undo) whenever a
 * single rad-initiated transaction removes >= BULK_FIELD_DELETE_TOAST_THRESHOLD
 * field nodes. Returns the unsubscribe cleanup.
 *
 * Skipped transactions:
 * - history replays (`history$` meta) — undoing an insert removes fields but
 *   is the rad explicitly asking for that removal;
 * - transactions tagged BULK_DELETE_TOAST_SKIP_META (draft reset, impression
 *   re-stream) — programmatic rebuilds, not deletions the rad performed.
 */
export function attachBulkFieldDeleteToast(editor: Editor): () => void {
  const onUpdate = ({ transaction }: { transaction: Transaction }) => {
    if (!transaction.docChanged) return;
    if (transaction.getMeta("history$")) return;
    if (transaction.getMeta(BULK_DELETE_TOAST_SKIP_META)) return;
    const removed = countFieldNodes(transaction.before) - countFieldNodes(transaction.doc);
    if (removed < BULK_FIELD_DELETE_TOAST_THRESHOLD) return;
    emit("toast", {
      message: `Removed ${removed} findings`,
      action: { label: "Undo", onPress: () => editor.commands.undo() },
      durationMs: UNDO_TOAST_DURATION_MS,
    });
  };
  editor.on("update", onUpdate);
  return () => {
    editor.off("update", onUpdate);
  };
}
